/**
 * Router de Workflows de Guías
 *
 * Punto de entrada único para ejecutar cualquier workflow post-DTE.
 * Para agregar un nuevo workflow, solo hay que:
 * 1. Crear la carpeta con la implementación
 * 2. Registrarlo en registry.js
 */

import { getWorkflowConfig, getAvailableCategories } from './registry';

export class WorkflowRouter {
	/**
	 * Obtiene las categorías de guía disponibles
	 */
	static getCategories() {
		return getAvailableCategories();
	}

	/**
	 * Obtiene la configuración de un workflow específico
	 */
	static getConfig(category) {
		return getWorkflowConfig(category);
	}

	/**
	 * Obtiene los campos adicionales para Formik
	 */
	static getAdditionalFields(category) {
		const workflow = getWorkflowConfig(category);
		return workflow?.config?.additionalFields || null;
	}

	/**
	 * Obtiene el componente de sección adicional
	 */
	static getSectionComponent(category) {
		const workflow = getWorkflowConfig(category);
		return workflow?.EPPSection || workflow?.Section || null;
	}

	/**
	 * Ejecuta validación pre-submit
	 */
	static async preValidate(category, formValues) {
		const workflow = getWorkflowConfig(category);
		if (workflow?.preValidate) {
			return await workflow.preValidate(formValues);
		}
		return { valid: true, errors: [] };
	}

	/**
	 * Ejecuta el workflow post-DTE
	 * @param {Function} onProgress - Callback (stepId, status, message?) para reportar progreso
	 */
	static async execute(dispatch, category, dteResult, formValues, onProgress) {
		const workflow = getWorkflowConfig(category);

		if (!workflow) {
			console.warn(`Workflow no encontrado para categoría: ${category}`);
			return null;
		}

		if (!workflow.execute) {
			console.warn(`Workflow ${category} no tiene ejecutor definido`);
			return null;
		}

		return await workflow.execute(dispatch, dteResult, formValues, onProgress);
	}

	/**
	 * Obtiene los pasos de progreso que ejecutará el workflow
	 */
	static getProgressSteps(category, formValues) {
		const workflow = getWorkflowConfig(category);
		if (workflow?.getProgressSteps) {
			return workflow.getProgressSteps(formValues);
		}
		return [];
	}

	/**
	 * Obtiene los pasos de rollback para un workflow
	 */
	static getRollbackSteps(category, formValues) {
		const workflow = getWorkflowConfig(category);
		if (workflow?.getRollbackSteps) {
			return workflow.getRollbackSteps(formValues);
		}
		return [{ id: 'rb_annul_guide', label: 'Anulando guía interna' }];
	}

	/**
	 * Verifica si una categoría tiene workflow asociado
	 */
	static hasWorkflow(category) {
		const workflow = getWorkflowConfig(category);
		return !!workflow?.execute;
	}

	/**
	 * Obtiene los tipos de operación disponibles para una categoría
	 */
	static getOperationTypes(category) {
		const workflow = getWorkflowConfig(category);
		return workflow?.config?.operationTypes || [];
	}
}

export { getAvailableCategories, getWorkflowConfig };

